import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type AppUiValue = {
  /** Small UI flag: e.g. mobile nav / side panel pattern */
  navDrawerOpen: boolean;
  setNavDrawerOpen: (open: boolean) => void;
  toggleNavDrawer: () => void;
};

const AppUiContext = createContext<AppUiValue | null>(null);

/**
 * Lightweight client-only UI state (context + hook). Use TanStack Query for server state.
 */
export function AppUiProvider({ children }: { children: React.ReactNode }) {
  const [navDrawerOpen, setNavDrawerOpen] = useState(true);

  const toggleNavDrawer = useCallback(() => {
    setNavDrawerOpen((o) => !o);
  }, []);

  const value = useMemo(
    () => ({
      navDrawerOpen,
      setNavDrawerOpen,
      toggleNavDrawer,
    }),
    [navDrawerOpen, toggleNavDrawer],
  );

  return <AppUiContext.Provider value={value}>{children}</AppUiContext.Provider>;
}

export function useAppUi(): AppUiValue {
  const ctx = useContext(AppUiContext);
  if (!ctx) throw new Error('useAppUi must be used within AppUiProvider');
  return ctx;
}
